import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    Input,
    Label,
    Switch,
    TextArea,
    TextField,
} from '@heroui/react';
import { DatePickerField } from '@/components/datePickerField';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { RateText } from '../../salariesMeta';
import type { EmployeeFormModel } from './useEmployeeForm';

type EmployeeFormViewProps = {
    form: EmployeeFormModel;
    onCancel: () => void;
    /** Today's rates; `null` when no rate set is in force. */
    rates: RateText | null;
};

/** Field-level error text, shown only once the user has tried to submit. */
const useErrorText = (form: EmployeeFormModel) => {
    const { t } = useTranslation('pages');
    return (field: string): string | null => {
        const key = form.errors[field];
        return key && form.submitted ? t(key) : null;
    };
};

/**
 * Render-only add / edit employee form.
 *
 * Two things on it carry more weight than their size suggests:
 *
 * - **Pension participation** is a switch with its statutory meaning spelled out
 *   and a free-text reason beside it. Turning it off removes the employee's 2%
 *   *and* the employer's 2% — that is what the description says, because a
 *   toggle labelled only "Pension" reads as if it affected one side.
 * - **Termination** is a date, not a delete. The note under it says so: the
 *   employee stays in the register under the Terminated filter, with everything
 *   they earned.
 */
export const EmployeeFormView: React.FC<EmployeeFormViewProps> = ({
    form,
    onCancel,
    rates,
}) => {
    const { t } = useTranslation('pages');
    const errorText = useErrorText(form);

    return (
        <div
            className="flex flex-col gap-5"
            {...getNodeTestIdProp('salaries-form')}
        >
            {/* Bilingual name */}
            <div className="flex flex-col gap-1">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <TextField
                        className="flex flex-col gap-1"
                        onChange={form.onNameKaChange}
                        value={form.nameKa}
                        {...getNodeTestIdProp('salaries-form-name-ka')}
                    >
                        <Label>{t('salaries.form.fields.nameKa')}</Label>
                        <Input
                            placeholder={t(
                                'salaries.form.fields.namePlaceholder',
                            )}
                        />
                    </TextField>
                    <TextField
                        className="flex flex-col gap-1"
                        onChange={form.onNameEnChange}
                        value={form.nameEn}
                        {...getNodeTestIdProp('salaries-form-name-en')}
                    >
                        <Label>{t('salaries.form.fields.nameEn')}</Label>
                        <Input
                            placeholder={t(
                                'salaries.form.fields.namePlaceholder',
                            )}
                        />
                    </TextField>
                </div>
                {errorText('name') ? (
                    <span
                        className="text-xs text-danger"
                        {...getNodeTestIdProp('salaries-form-name-error')}
                    >
                        {errorText('name')}
                    </span>
                ) : null}
            </div>

            {/* Personal ID — bounded, not full-width: it is exactly 11 digits */}
            <TextField
                className="flex w-full max-w-[15rem] flex-col gap-1"
                isInvalid={errorText('personalId') !== null}
                onChange={form.onPersonalIdChange}
                value={form.personalId}
                {...getNodeTestIdProp('salaries-form-personal-id')}
            >
                <Label>{t('salaries.form.fields.personalId')}</Label>
                <Input
                    className="tabular-nums"
                    inputMode="numeric"
                    placeholder="01001012345"
                />
                <span className="text-xs text-muted">
                    {t('salaries.form.fields.personalIdHint')}
                </span>
                {errorText('personalId') ? (
                    <span
                        className="text-xs text-danger"
                        {...getNodeTestIdProp(
                            'salaries-form-personal-id-error',
                        )}
                    >
                        {errorText('personalId')}
                    </span>
                ) : null}
            </TextField>

            {/* Bilingual position */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextField
                    className="flex flex-col gap-1"
                    onChange={form.onPositionKaChange}
                    value={form.positionKa}
                    {...getNodeTestIdProp('salaries-form-position-ka')}
                >
                    <Label>{t('salaries.form.fields.positionKa')}</Label>
                    <Input
                        placeholder={t(
                            'salaries.form.fields.positionPlaceholder',
                        )}
                    />
                </TextField>
                <TextField
                    className="flex flex-col gap-1"
                    onChange={form.onPositionEnChange}
                    value={form.positionEn}
                    {...getNodeTestIdProp('salaries-form-position-en')}
                >
                    <Label>{t('salaries.form.fields.positionEn')}</Label>
                    <Input
                        placeholder={t(
                            'salaries.form.fields.positionPlaceholder',
                        )}
                    />
                </TextField>
            </div>

            {/* Hire date + gross salary */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DatePickerField
                    id="salaries-form-hire-date"
                    label={t('salaries.form.fields.hireDate')}
                    name="hireDate"
                    onChange={form.onHireDateChange}
                    value={form.hireDate}
                    {...(errorText('hireDate')
                        ? { error: errorText('hireDate') as string }
                        : {})}
                />

                <TextField
                    className="flex flex-col gap-1"
                    isInvalid={errorText('grossSalary') !== null}
                    onChange={form.onGrossSalaryChange}
                    value={form.grossSalary}
                    {...getNodeTestIdProp('salaries-form-gross')}
                >
                    <Label>{t('salaries.form.fields.grossSalary')}</Label>
                    <Input
                        className="text-right tabular-nums"
                        inputMode="decimal"
                        placeholder="0.00"
                    />
                    <span className="text-xs text-muted">
                        {t('salaries.form.fields.grossHint')}
                    </span>
                    {errorText('grossSalary') ? (
                        <span className="text-xs text-danger">
                            {errorText('grossSalary')}
                        </span>
                    ) : null}
                </TextField>
            </div>

            {/* Pension participation — the switch trails its row, per the
                system's convention, and its description states what turning it
                off actually removes. */}
            <div className="flex flex-col gap-2 rounded-xl bg-surface-secondary p-3">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-sm font-medium text-foreground">
                            {t('salaries.form.fields.pensionParticipant')}
                        </span>
                        <span className="text-xs text-muted">
                            {rates
                                ? t('salaries.form.fields.pensionHint', {
                                      employee: rates.employeePension,
                                      employer: rates.employerPension,
                                  })
                                : t('salaries.form.fields.pensionHintNoRate')}
                        </span>
                    </div>
                    <Switch
                        aria-label={t(
                            'salaries.form.fields.pensionParticipant',
                        )}
                        isSelected={form.pensionParticipant}
                        onChange={form.onPensionParticipantChange}
                        {...getNodeTestIdProp('salaries-form-pension')}
                    >
                        <Switch.Content>
                            <Switch.Control>
                                <Switch.Thumb />
                            </Switch.Control>
                        </Switch.Content>
                    </Switch>
                </div>

                <TextField
                    className="flex flex-col gap-1"
                    onChange={form.onPensionNoteChange}
                    value={form.pensionNote}
                    {...getNodeTestIdProp('salaries-form-pension-note')}
                >
                    <Label className="text-xs text-muted">
                        {t('salaries.form.fields.pensionNote')}
                    </Label>
                    <TextArea
                        placeholder={t(
                            'salaries.form.fields.pensionNotePlaceholder',
                        )}
                        rows={2}
                        variant="secondary"
                    />
                </TextField>
            </div>

            {/* Termination — a date, never a delete */}
            <div className="flex flex-col gap-1">
                <DatePickerField
                    id="salaries-form-termination-date"
                    label={t('salaries.form.fields.terminationDate')}
                    name="terminationDate"
                    onChange={form.onTerminationDateChange}
                    value={form.terminationDate}
                    {...(errorText('terminationDate')
                        ? { error: errorText('terminationDate') as string }
                        : {})}
                />
                <span className="text-xs text-muted">
                    {t('salaries.form.fields.terminationHint')}
                </span>
                {form.willTerminate ? (
                    <span
                        className="flex items-start gap-2 rounded-lg bg-warning-soft px-3 py-2 text-xs text-warning-soft-foreground"
                        {...getNodeTestIdProp('salaries-form-termination-note')}
                    >
                        <Icon aria-hidden icon="gravity-ui:circle-info" />
                        {t('salaries.form.fields.terminationConfirm')}
                    </span>
                ) : null}
            </div>

            {form.saveError ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('salaries-form-error')}
                >
                    {form.saveError}
                </p>
            ) : null}

            <div className="flex items-center justify-end gap-2">
                <Button
                    onPress={onCancel}
                    variant="tertiary"
                    {...getNodeTestIdProp('salaries-form-cancel')}
                >
                    {t('salaries.actions.cancel')}
                </Button>
                <Button
                    isPending={form.isSaving}
                    onPress={form.onSubmit}
                    variant="primary"
                    {...getNodeTestIdProp('salaries-form-save')}
                >
                    <Icon aria-hidden icon="gravity-ui:check" />
                    {form.isEdit
                        ? t('salaries.actions.saveChanges')
                        : t('salaries.actions.createEmployee')}
                </Button>
            </div>

            {/* Removal, on an edit only — and below the save actions, separated
                by a rule, so the destructive control is nowhere near the one the
                user came here to press. Same placement the account form gives
                Archive. */}
            {form.isEdit ? (
                <div className="flex flex-col gap-2 border-t border-separator pt-4">
                    {form.isDeleteArmed ? (
                        <div className="flex flex-wrap items-center gap-3">
                            <span
                                className="text-sm text-foreground"
                                {...getNodeTestIdProp(
                                    'salaries-form-delete-confirm',
                                )}
                            >
                                {t('salaries.form.delete.confirm')}
                            </span>
                            <Button
                                isPending={form.isDeleting}
                                onPress={form.onDelete}
                                size="sm"
                                variant="danger"
                                {...getNodeTestIdProp(
                                    'salaries-form-delete-do',
                                )}
                            >
                                {t('salaries.actions.deleteEmployee')}
                            </Button>
                            <Button
                                onPress={form.onDeleteCancel}
                                size="sm"
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'salaries-form-delete-cancel',
                                )}
                            >
                                {t('salaries.actions.keepEmployee')}
                            </Button>
                        </div>
                    ) : (
                        <Button
                            className="w-fit"
                            onPress={form.onDeleteArm}
                            variant="danger-soft"
                            {...getNodeTestIdProp('salaries-form-delete')}
                        >
                            <Icon aria-hidden icon="gravity-ui:trash-bin" />
                            {t('salaries.actions.deleteEmployee')}
                        </Button>
                    )}

                    {/* Stated up front rather than only on the refusal: an
                        accountant reaching for Delete when they mean "they have
                        left" should read this before pressing anything. */}
                    <span
                        className="text-xs text-muted"
                        {...getNodeTestIdProp('salaries-form-delete-note')}
                    >
                        {t('salaries.form.delete.note')}
                    </span>

                    {form.deleteError ? (
                        <span
                            className="text-xs text-danger"
                            {...getNodeTestIdProp('salaries-form-delete-error')}
                        >
                            {form.deleteError}
                        </span>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};
